import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { ChevronDown, Building2, Store, Check, Search } from "lucide-react";

/**
 * Unified workspace picker — one compact control for organization + branch.
 *
 * Behaviour is unchanged from OrgBranchSelector: selecting an org calls
 * ``selectOrg`` (which resets branch), selecting a branch calls
 * ``selectBranch(id | null)``.
 */
export default function WorkspaceSelector() {
  const { orgId, branchId, selectOrg, selectBranch, memberships } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [branches, setBranches] = useState([]);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const rootRef = useRef(null);

  useEffect(() => {
    api.get("/orgs").then(({ data }) => setOrgs(data)).catch(() => {});
  }, [memberships.length]);

  useEffect(() => {
    if (!orgId) { setBranches([]); return; }
    api.get("/branches").then(({ data }) => setBranches(data)).catch(() => setBranches([]));
  }, [orgId]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const activeOrg = orgs.find((o) => o.id === orgId);
  const activeBranch = branches.find((b) => b.id === branchId);

  const filteredOrgs = orgs.filter((o) =>
    !q || o.name.toLowerCase().includes(q.toLowerCase()));
  const filteredBranches = branches.filter((b) =>
    !q || b.name.toLowerCase().includes(q.toLowerCase())
      || (b.code || "").toLowerCase().includes(q.toLowerCase()));

  const primary = activeOrg?.name || "Select workspace";
  const secondary = activeBranch?.name || (orgId ? "All branches" : "Choose an organization");

  return (
    <div className="relative w-full" ref={rootRef} data-testid="workspace-selector">
      <button
        type="button"
        data-testid="workspace-selector-btn"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="group w-full h-[42px] flex items-center gap-3 pl-3 pr-2.5 rounded-[10px] bg-foreground/[0.03] hover:bg-foreground/[0.06] border border-transparent hover:border-foreground/10 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
      >
        <span className="h-7 w-7 shrink-0 grid place-items-center rounded-md bg-foreground/[0.06] group-hover:bg-foreground/[0.09] transition-colors">
          <Building2 size={14} className="ink-muted group-hover:ink" />
        </span>
        <span className="flex-1 min-w-0 text-left leading-tight">
          <span className="block text-[13px] font-semibold ink truncate" data-testid="workspace-org-name">
            {primary}
          </span>
          <span className="block text-[11px] ink-muted truncate mt-0.5" data-testid="workspace-branch-name">
            {secondary}
          </span>
        </span>
        <ChevronDown
          size={15}
          className={`shrink-0 ink-muted transition-transform duration-150 ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div
          className="absolute z-50 mt-2 left-0 right-0 min-w-[320px] rounded-xl border hairline bg-popover text-popover-foreground shadow-2xl backdrop-blur-xl overflow-hidden"
          data-testid="workspace-selector-panel"
          role="menu"
        >
          <div className="px-3 pt-3 pb-2 flex items-center gap-2 border-b hairline-soft">
            <Search size={13} className="ink-muted" />
            <input
              type="text"
              placeholder="Search workspaces…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="flex-1 bg-transparent outline-none text-[13px] ink placeholder:ink-subtle"
              data-testid="workspace-search-input"
              autoFocus
            />
          </div>

          <div className="max-h-[420px] overflow-y-auto py-1.5">
            <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-[0.08em] ink-subtle font-medium">
              Organization
            </div>
            {filteredOrgs.length === 0 && (
              <div className="px-3 py-2 text-[12px] ink-muted">No matches.</div>
            )}
            {filteredOrgs.map((o) => (
              <button
                key={o.id}
                type="button"
                role="menuitem"
                data-testid={`workspace-org-option-${o.id}`}
                onClick={() => { selectOrg(o.id); setQ(""); }}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-left text-[13px] transition-colors ${
                  o.id === orgId ? "bg-primary/10 ink" : "ink hover:bg-foreground/[0.05]"
                }`}
              >
                <Building2 size={13} className="ink-muted shrink-0" />
                <span className="flex-1 min-w-0">
                  <span className="block truncate font-medium">{o.name}</span>
                  <span className="block text-[10.5px] ink-muted truncate mt-0.5">
                    {o.business_type}{o.country ? ` · ${o.country}` : ""}
                  </span>
                </span>
                {o.id === orgId && <Check size={13} className="text-primary shrink-0" />}
              </button>
            ))}

            {orgId && (
              <>
                <div className="mt-1.5 px-3 pt-2 pb-1 text-[10px] uppercase tracking-[0.08em] ink-subtle font-medium border-t hairline-soft">
                  Branch
                </div>
                <button
                  type="button"
                  role="menuitem"
                  data-testid="workspace-branch-option-all"
                  onClick={() => { selectBranch(null); setOpen(false); }}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 text-left text-[13px] transition-colors ${
                    !branchId ? "bg-secondary/10 ink" : "ink hover:bg-foreground/[0.05]"
                  }`}
                >
                  <Store size={13} className="ink-muted shrink-0" />
                  <span className="flex-1 min-w-0">
                    <span className="block truncate font-medium">All branches</span>
                    <span className="block text-[10.5px] ink-muted truncate mt-0.5">
                      Aggregate view across every branch
                    </span>
                  </span>
                  {!branchId && <Check size={13} className="text-secondary shrink-0" />}
                </button>
                {filteredBranches.map((b) => (
                  <button
                    key={b.id}
                    type="button"
                    role="menuitem"
                    data-testid={`workspace-branch-option-${b.id}`}
                    onClick={() => { selectBranch(b.id); setOpen(false); }}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-left text-[13px] transition-colors ${
                      b.id === branchId ? "bg-secondary/10 ink" : "ink hover:bg-foreground/[0.05]"
                    }`}
                  >
                    <Store size={13} className="ink-muted shrink-0" />
                    <span className="flex-1 min-w-0">
                      <span className="block truncate font-medium">{b.name}</span>
                      <span className="block text-[10.5px] ink-muted truncate mt-0.5">
                        {b.code}{b.venue_capacity ? ` · cap ${b.venue_capacity}` : ""}
                      </span>
                    </span>
                    {b.id === branchId && <Check size={13} className="text-secondary shrink-0" />}
                  </button>
                ))}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
